import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, ArrowRight, ShieldCheck } from 'lucide-react'; // Importando do lucide-react!
import { getLandingPerfil } from '../utils/perfisRestritos';

function Login() {
  // --- Estados Originais ---
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  // --- Redefinir Senha ---
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [modalError, setModalError] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [destination, setDestination] = useState('/home');
  
  // --- Verificar Senha Forte ---
  const hasMinLength = (pwd) => pwd.length >= 6;
  const hasUpperCase = (pwd) => /[A-Z]/.test(pwd);
  const hasSpecialChar = (pwd) => /[!@#$&*]/.test(pwd);

  // Destino padrão após o login: a Home é a página inicial única para TODOS
  // os perfis (decisão de 2026-06-15) — EXCETO os perfis restritos, que não têm
  // Home e caem no próprio landing (ex.: Coordenador de Qualidade → /financeiro).
  // getLandingPerfil devolve '/home' para os perfis normais.
  const getDefaultRoute = (perfil) => getLandingPerfil(perfil);

  const navigate = useNavigate();
  const location = useLocation();

  // Se o interceptor (authInterceptor.js) redirecionou por sessão expirada,
  // mostra o aviso e limpa o flag para não reaparecer em recargas seguintes.
  useEffect(() => {
    if (sessionStorage.getItem('sessaoExpirada')) {
      setError('Sua sessão expirou. Faça login novamente.');
      sessionStorage.removeItem('sessaoExpirada');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); 

    try {
        const response = await fetch('/api/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Erro ao tentar fazer login');
        }

        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        const defaultRoute = getDefaultRoute(data.user?.perfil);
        // ⚠️⚠️ A QUERY STRING VIAJA JUNTO COM O CAMINHO (`search`), e não é
        //    detalhe: até 2026-08-27 só o `pathname` era preservado, então um
        //    link direto como `/chamados?tab=abrir` (o que o manual GPS-TI usa
        //    para levar a loja direto ao formulário) perdia o `?tab=abrir` no
        //    login e a pessoa caía na Visão Geral do módulo.
        //    ⚠️ O sintoma é traiçoeiro: funciona perfeitamente para quem JÁ está
        //    logado e falha só para quem não está — que é justamente quem vem
        //    de fora, pelo link.
        // Para onde ir depois de entrar, em ordem de prioridade:
        //  1. state.from — quem foi barrado pelo PrivateRoute no fluxo normal
        //     (sem recarga de página): o React Router preserva a origem em
        //     memória. É o caminho de quem chega DESLOGADO (localStorage limpo).
        //  2. redirectAposLogin (sessionStorage) — quem chegou aqui por um
        //     redirect DURO do authInterceptor (sessão expirada / token velho no
        //     celular do gerente). O state.from não sobrevive ao window.location,
        //     mas o sessionStorage sim. É o que faz o QR da etiqueta (/q/:codigo)
        //     levar de volta ao equipamento depois de reautenticar, em vez de
        //     despejar na Home.
        //  3. landing do perfil (padrão), quando não há origem nenhuma.
        const origem = location.state?.from;
        const salvo = sessionStorage.getItem('redirectAposLogin');
        let finalDestination = defaultRoute;
        if (origem) {
            finalDestination = `${origem.pathname}${origem.search || ''}`;
        } else if (salvo) {
            finalDestination = salvo;
        }
        // Consumido na saída: some para não sequestrar um login manual posterior
        // na mesma aba (ex.: a pessoa abandonou o retorno e voltou ao login depois).
        sessionStorage.removeItem('redirectAposLogin');
        setDestination(finalDestination);

        // --- LÓGICA DE SENHA EXPIRADA OU PRIMEIRO ACESSO ---
        if (data.primeiroAcesso || data.senhaExpirada) {
            setTempToken(data.token);
            setModalError(data.senhaExpirada ? "Sua senha expirou (limite de 90 dias). Por favor, crie uma nova." : "");
            setShowResetModal(true);
        } else {
            navigate(finalDestination, { replace: true });
        }

    } catch (err) {
        setError(err.message);
    }
};

  const handlePasswordReset = async () => {
    setModalError('');

    if (newPassword !== confirmPassword) {
      setModalError('As senhas não coincidem.');
      return;
    }
    if (newPassword.length < 6) {
      setModalError('A senha deve ter no mínimo 6 caracteres.');
      return;
    }
    const regex = /^(?=.*[A-Z])(?=.*[!@#$&*])/;
    if (!regex.test(newPassword)) {
      setModalError('A senha deve conter pelo menos uma letra maiúscula e um caractere especial (!@#$&*).');
      return;
    }

    try {
      const response = await fetch('/api/update-password', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}` 
        },
        body: JSON.stringify({ novaSenha: newPassword }),
      });

      if (response.ok) {
        alert('Senha redefinida com sucesso! Você será redirecionado.');
        setShowResetModal(false);
        navigate(destination, { replace: true });
      } else {
        const data = await response.json();
        setModalError(data.message || 'Erro ao atualizar senha.');
      }
    } catch (err) {
      setModalError('Erro de conexão ao atualizar senha.');
    }
  };

  return (
    <div className="login-page-wrapper">
      
      {/* Cartão Centralizado Estilo SaaS com Cores Originais */}
      <div className="login-premium-card">
        
        {/* Lado Esquerdo - Branding (Fundo Branco apenas com o Logo) */}
        <div className="login-brand-side">
          <div className="brand-content">
            <img src="/icon-logotipo.png" alt="Grupo Correia" className="brand-logo" />
          </div>
        </div>

        {/* Lado Direito - Formulário (Fundo Vermelho com Destaques Amarelos) */}
        <div className="login-form-side">
          <div className="form-header">
            <div className="icon-badge">
              <ShieldCheck size={28} color="#BC0005" /> 
            </div>
            <h3>Acesse sua Conta</h3>
            <p>Insira suas credenciais para continuar</p>
          </div>

          <form onSubmit={handleSubmit} className="modern-login-form">
            <div className="input-group">
              <label htmlFor="username">Usuário</label>
              <div className="input-with-icon">
                <User size={20} className="input-icon" />
                <input
                  type="text"
                  id="username"
                  placeholder="Seu nome de usuário"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="password">Senha</label>
              <div className="input-with-icon">
                <Lock size={20} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button 
                  type="button" 
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Link para o fluxo público "Esqueci a senha" (recuperação por WhatsApp) */}
            <div className="form-actions">
              <Link to="/esqueci-senha" className="forgot-link">Esqueci minha senha</Link>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="btn-primary-login">
              Entrar <ArrowRight size={20} />
            </button>
          </form>
        </div>
      </div>

      {/* --- MODAL DE PRIMEIRO ACESSO / REDEFINIR SENHA --- */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <ShieldCheck size={32} color="#BC0005" />
              <h3 style={{ color: '#0f172a' }}>Definir Nova Senha</h3>
              <p style={{ color: '#64748b' }}>Por questões de segurança, crie uma senha forte.</p>
            </div>
            
            <div className="password-rules">
              <div className={hasMinLength(newPassword) ? 'rule valid' : 'rule'}>
                {hasMinLength(newPassword) ? '✓' : '○'} Mín. de 6 caracteres
              </div>
              <div className={hasUpperCase(newPassword) ? 'rule valid' : 'rule'}>
                {hasUpperCase(newPassword) ? '✓' : '○'} 1 Letra Maiúscula
              </div>
              <div className={hasSpecialChar(newPassword) ? 'rule valid' : 'rule'}>
                {hasSpecialChar(newPassword) ? '✓' : '○'} 1 Caractere Especial (!@#$&*)
              </div>
            </div>

            {modalError && <div className="error-message">{modalError}</div>}

            <div className="input-group">
              <label style={{ color: '#334155' }}>Nova Senha</label>
              <div className="input-with-icon">
                <Lock size={20} className="input-icon" />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Sua nova senha"
                  style={{ border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}
                />
                <button type="button" className="toggle-password" onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="input-group">
              <label style={{ color: '#334155' }}>Confirmar Senha</label>
              <div className="input-with-icon">
                <Lock size={20} className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirme a senha"
                  style={{ border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}
                />
                <button type="button" className="toggle-password" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button 
              onClick={handlePasswordReset}
              disabled={!(hasMinLength(newPassword) && hasUpperCase(newPassword) && hasSpecialChar(newPassword))}
              className="btn-primary-login"
              style={{ marginTop: '20px' }}
            >
              Confirmar e Acessar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;